import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { I18NService } from '@core';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { finalize } from 'rxjs';
import { HttpService } from 'src/app/http/http.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { ConstantUtil } from 'src/app/util/constant.util';

type AssistantAppStatus = 'Draft' | 'Published' | 'Unpublished';
type AssistantResponseMode = 'Qa' | 'CollectOnly' | 'Batch';
type OperationsPeriod = 'today' | 'thisWeek' | 'thisMonth' | 'custom';

interface AssistantDurationPackage {
  packageId?: string;
  name?: string;
  durationDays: number;
  priceCny: number;
  recommended: boolean;
}

interface AssistantBatchTrigger {
  triggerMessageCount?: number;
  triggerIntervalSeconds?: number;
  triggerKeywords?: string[];
}

interface AssistantApp {
  id: string;
  name: string;
  providerName?: string;
  shortDescription?: string;
  description: string;
  iconUrl?: string;
  category: string;
  customCategory?: string;
  version: string;
  status: AssistantAppStatus;
  webhookUrl: string;
  responseMode: AssistantResponseMode;
  messageTypes: string[];
  senderTypes: string[];
  replyReceiverTypes?: string[];
  batchTrigger?: AssistantBatchTrigger;
  durationPackages: AssistantDurationPackage[];
  featureTags?: string[];
  subscriberCount?: number;
  subscriptionCount?: number;
  updateTime?: number;
}

interface AssistantAppListResponse {
  data?: AssistantApp[] | { list?: AssistantApp[]; records?: AssistantApp[]; items?: AssistantApp[] };
  success?: boolean;
  code?: number;
}

interface AssistantStoreOperationKpi {
  totalTemplateCount: number;
  totalSubscriptionCount: number;
  activeSubscriptionCount: number;
  totalTransactionAmount: number;
  cashIncome: number;
  uuponDeduction: number;
  purchaseCount: number;
  renewalCount: number;
  activeInstanceCount: number;
  trialConversionCount: number;
  templatePackages: AssistantStoreOperationTemplatePackageKpi[];
  templates: AssistantStoreOperationTemplateKpi[];
  lastUpdatedTime?: number;
}

interface AssistantStoreOperationTemplateKpi {
  templateId: string;
  templateName?: string;
  category?: string;
  version?: string;
  totalSubscriptionCount: number;
  activeSubscriptionCount: number;
  transactionAmount: number;
  cashIncome: number;
  uuponDeduction: number;
}

interface AssistantStoreOperationTemplatePackageKpi {
  templateId: string;
  templateName?: string;
  providerName?: string;
  category?: string;
  durationPackageId: string;
  durationPackageName?: string;
  salesCount: number;
  transactionAmount: number;
  cashIncome: number;
  uuponDeduction: number;
  version?: string;
  subscriptionCount?: number;
  totalSubscriptionCount?: number;
  activeSubscriptionCount?: number;
  ratingDistribution?: AssistantStoreRatingItem[] | Record<string, number>;
}

interface AssistantStoreOperationKpiResponse {
  data?: AssistantStoreOperationKpi;
  success?: boolean;
  code?: number;
}

interface AssistantStoreRatingItem {
  stars: number;
  percentage: number;
}

interface AssistantStoreOperationRow {
  templateId: string;
  name: string;
  category: string;
  version: string;
  totalSubscriptions: number;
  activeSubscriptions: number;
  ratingDistribution: AssistantStoreRatingItem[];
  transactionAmount: number;
  cashIncome: number;
  uuponDeduction: number;
}

const CUSTOM_CATEGORY = 'Custom';
const CATEGORIES = ['客服支持', '销售转化', '营销推广', '运营管理', '数据分析', CUSTOM_CATEGORY];
const RESPONSE_MODE_OPTIONS: Array<{ value: AssistantResponseMode; labelKey: string }> = [
  { value: 'Qa', labelKey: 'assistant.store.response.qa' },
  { value: 'CollectOnly', labelKey: 'assistant.store.response.collect-only' },
  { value: 'Batch', labelKey: 'assistant.store.response.batch' }
];
const MESSAGE_TYPE_OPTIONS = [
  { value: 'Text', labelKey: 'assistant.store.message-type.text' },
  { value: 'Image', labelKey: 'assistant.store.message-type.image' },
  { value: 'File', labelKey: 'assistant.store.message-type.file' },
  { value: 'Audio', labelKey: 'assistant.store.message-type.audio' },
  { value: 'Video', labelKey: 'assistant.store.message-type.video' }
];
const SENDER_TYPE_OPTIONS = [
  { value: 'Employee', labelKey: 'assistant.store.sender-type.employee' },
  { value: 'Customer', labelKey: 'assistant.store.sender-type.customer' }
];

@Component({
  selector: 'app-official-assistant-store',
  standalone: true,
  imports: [
    CommonModule,
    I18nPipe,
    FormsModule,
    ReactiveFormsModule,
    NzButtonModule,
    NzCheckboxModule,
    NzDrawerModule,
    NzDatePickerModule,
    NzEmptyModule,
    NzFormModule,
    NzIconModule,
    NzInputModule,
    NzSelectModule,
    NzSpinModule,
    NzTagModule,
    NzToolTipModule,
    TestIdDirective
  ],
  templateUrl: './official-assistant-store.component.html',
  styleUrls: ['./official-assistant-store.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OfficialAssistantStoreComponent implements OnInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);
  private readonly http = inject(HttpService);
  private readonly message = inject(NzMessageService);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly categories = CATEGORIES;
  readonly responseModeOptions = RESPONSE_MODE_OPTIONS;
  readonly messageTypeOptions = MESSAGE_TYPE_OPTIONS;
  readonly senderTypeOptions = SENDER_TYPE_OPTIONS;
  readonly operationsPeriodOptions: Array<{ value: OperationsPeriod; labelKey: string }> = [
    { value: 'today', labelKey: 'assistant.store.operations.filter.today' },
    { value: 'thisWeek', labelKey: 'assistant.store.operations.filter.this-week' },
    { value: 'thisMonth', labelKey: 'assistant.store.operations.filter.this-month' },
    { value: 'custom', labelKey: 'assistant.store.operations.filter.custom' }
  ];
  readonly operationsSkeletonCards = [0, 1, 2, 3, 4];
  readonly operationsSkeletonRows = [0, 1, 2, 3];
  readonly operationsSkeletonColumns = [0, 1, 2, 3, 4, 5, 6];
  apps: AssistantApp[] = [];
  filteredApps: AssistantApp[] = [];
  loading = false;
  saving = false;
  operatingId: string | null = null;
  modalVisible = false;
  activeTab: 'templates' | 'operations' = 'templates';
  operationsLoading = false;
  operationsKpi: AssistantStoreOperationKpi | null = null;
  operationsPeriod: OperationsPeriod = 'thisMonth';
  operationsPeriodMenuOpen = false;
  operationsDateRange: Date[] | null = null;
  operationsCategory: string | null = null;
  operationsCategoryMenuOpen = false;
  operationsKeyword = '';
  editingApp: AssistantApp | null = null;
  keyword = '';
  statusFilter: AssistantAppStatus | null = null;
  categoryFilter: string | null = null;
  iconPreview = '';
  private iconFile: File | null = null;
  private iconObjectUrl = '';
  private operationsRefreshTimer?: ReturnType<typeof setInterval>;

  readonly form = this.fb.nonNullable.group({
    id: [''],
    name: ['', [Validators.required, Validators.maxLength(50)]],
    shortDescription: ['', Validators.maxLength(100)],
    description: ['', [Validators.required, Validators.maxLength(200)]],
    category: [CATEGORIES[0], Validators.required],
    customCategory: [''],
    version: ['1.0', Validators.required],
    webhookUrl: ['', [Validators.required, Validators.pattern(/^https:\/\//i)]],
    responseMode: this.fb.nonNullable.control<AssistantResponseMode>('Qa', Validators.required),
    batchTriggerMessageCount: [10, [Validators.required, Validators.min(1)]],
    batchTriggerIntervalMinutes: [60, [Validators.required, Validators.min(1)]],
    batchTriggerKeywordsText: [''],
    messageTypes: [['Text'], Validators.required],
    senderTypes: [['Customer'], Validators.required],
    replyReceiverTypes: this.fb.nonNullable.control<string[]>([]),
    featureTagsText: ['']
  });

  readonly packages = this.fb.array([
    this.createPackage(this.defaultPackageName(30), 30, 99, true),
    this.createPackage(this.defaultPackageName(90), 90, 269),
    this.createPackage(this.defaultPackageName(365), 365, 999)
  ]);

  constructor() {
    this.form.controls.category.valueChanges.pipe(takeUntilDestroyed()).subscribe(category => {
      this.updateCustomCategoryControl(category);
    });
    this.updateCustomCategoryControl(this.form.controls.category.value);
  }

  ngOnInit(): void {
    this.loadApps();
  }

  ngOnDestroy(): void {
    this.revokeIconObjectUrl();
    if (this.operationsRefreshTimer) clearInterval(this.operationsRefreshTimer);
  }

  get packageControls(): FormArray {
    return this.packages;
  }

  loadApps(): void {
    this.loading = true;
    this.http
      .post(ConstantUtil.ADMIN_ASSISTANT_STORE_V1_LIST, {})
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: response => {
          this.apps = this.readApps(response as AssistantAppListResponse);
          this.applyFilters();
        },
        error: () => this.message.error(this.t('assistant.store.message.load-failed'))
      });
  }

  refresh(): void {
    if (this.activeTab === 'templates') {
      this.loadApps();
    } else {
      this.loadOperationsKpi();
    }
  }

  selectTab(tab: 'templates' | 'operations'): void {
    this.activeTab = tab;
    if (tab === 'operations') this.loadOperationsKpi();
  }

  loadOperationsKpi(): void {
    this.operationsLoading = true;
    this.http
      .post(ConstantUtil.ADMIN_ASSISTANT_STORE_V1_OPERATION_KPI, this.createOperationsQuery())
      .pipe(
        finalize(() => {
          this.operationsLoading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: response => {
          this.operationsKpi = this.readOperationsKpi(response as AssistantStoreOperationKpiResponse);
          this.ensureOperationsRefreshTimer();
        },
        error: () => this.message.error(this.t('assistant.store.operations.message.load-failed'))
      });
  }

  applyOperationsFilters(): void {
    this.loadOperationsKpi();
  }

  changeOperationsPeriod(): void {
    if (this.operationsPeriod !== 'custom') this.operationsDateRange = null;
    this.loadOperationsKpi();
  }

  toggleOperationsPeriodMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.operationsCategoryMenuOpen = false;
    this.operationsPeriodMenuOpen = !this.operationsPeriodMenuOpen;
  }

  selectOperationsPeriod(period: OperationsPeriod): void {
    this.operationsPeriodMenuOpen = false;
    if (period === this.operationsPeriod) return;
    this.operationsPeriod = period;
    this.changeOperationsPeriod();
  }

  toggleOperationsCategoryMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.operationsPeriodMenuOpen = false;
    this.operationsCategoryMenuOpen = !this.operationsCategoryMenuOpen;
  }

  selectOperationsCategory(category: string | null): void {
    this.operationsCategoryMenuOpen = false;
    if (category === this.operationsCategory) return;
    this.operationsCategory = category;
    this.applyOperationsFilters();
  }

  @HostListener('document:click')
  closeOperationsMenus(): void {
    if (!this.operationsPeriodMenuOpen && !this.operationsCategoryMenuOpen) return;
    this.operationsPeriodMenuOpen = false;
    this.operationsCategoryMenuOpen = false;
    this.cdr.markForCheck();
  }

  get operationsPeriodLabelKey(): string {
    return (
      this.operationsPeriodOptions.find(option => option.value === this.operationsPeriod)?.labelKey ??
      'assistant.store.operations.filter.this-month'
    );
  }

  clearOperationsFilters(): void {
    this.operationsPeriod = 'thisMonth';
    this.operationsDateRange = null;
    this.operationsCategory = null;
    this.operationsKeyword = '';
    this.loadOperationsKpi();
  }

  get operationsCategories(): string[] {
    return [...new Set(this.apps.map(item => item.category).filter(Boolean))];
  }

  get operationsRows(): AssistantStoreOperationRow[] {
    const rows = new Map<string, AssistantStoreOperationRow>();
    const templateItems = this.operationsKpi?.templates ?? [];
    if (templateItems.length) {
      return templateItems.map(item => ({
        templateId: item.templateId,
        name: item.templateName || this.apps.find(candidate => candidate.id === item.templateId)?.name || item.templateId,
        category: item.category || this.apps.find(candidate => candidate.id === item.templateId)?.category || '-',
        version: item.version || this.apps.find(candidate => candidate.id === item.templateId)?.version || '-',
        totalSubscriptions: Number(item.totalSubscriptionCount) || 0,
        activeSubscriptions: Number(item.activeSubscriptionCount) || 0,
        ratingDistribution: [],
        transactionAmount: Number(item.transactionAmount) || 0,
        cashIncome: Number(item.cashIncome) || 0,
        uuponDeduction: Number(item.uuponDeduction) || 0
      }));
    }
    for (const item of this.operationsKpi?.templatePackages ?? []) {
      const app = this.apps.find(candidate => candidate.id === item.templateId);
      const existing = rows.get(item.templateId);
      const explicitSubscriptions = item.totalSubscriptionCount ?? item.subscriptionCount;
      const row: AssistantStoreOperationRow = existing ?? {
        templateId: item.templateId,
        name: item.templateName || app?.name || item.templateId,
        category: item.category || app?.customCategory || app?.category || '-',
        version: item.version || app?.version || '-',
        totalSubscriptions: 0,
        activeSubscriptions: 0,
        ratingDistribution: this.normalizeRatingDistribution(item.ratingDistribution),
        transactionAmount: 0,
        cashIncome: 0,
        uuponDeduction: 0
      };
      row.totalSubscriptions =
        explicitSubscriptions == null
          ? row.totalSubscriptions + (Number(item.salesCount) || 0)
          : Math.max(row.totalSubscriptions, Number(explicitSubscriptions) || 0);
      row.activeSubscriptions = Math.max(
        row.activeSubscriptions,
        Number(item.activeSubscriptionCount ?? app?.subscriberCount ?? app?.subscriptionCount) || 0
      );
      if (!this.hasRatingDistribution(row.ratingDistribution) && item.ratingDistribution) {
        row.ratingDistribution = this.normalizeRatingDistribution(item.ratingDistribution);
      }
      row.transactionAmount += Number(item.transactionAmount) || 0;
      row.cashIncome += Number(item.cashIncome) || 0;
      row.uuponDeduction += Number(item.uuponDeduction) || 0;
      rows.set(item.templateId, row);
    }
    return [...rows.values()];
  }

  formatMoney(value?: number): string {
    return new Intl.NumberFormat(this.i18n.currentLang || 'zh-TW', {
      style: 'currency',
      currency: 'TWD',
      maximumFractionDigits: 0
    }).format(value ?? 0);
  }

  hasRatingDistribution(items: AssistantStoreRatingItem[]): boolean {
    return items.some(item => item.percentage > 0);
  }

  applyFilters(): void {
    const keyword = this.keyword.trim().toLocaleLowerCase();
    this.filteredApps = this.apps.filter(app => {
      const matchesKeyword =
        !keyword ||
        [app.name, app.shortDescription, app.description, ...(app.featureTags ?? [])]
          .filter(Boolean)
          .join(' ')
          .toLocaleLowerCase()
          .includes(keyword);
      return (
        matchesKeyword &&
        (!this.statusFilter || app.status === this.statusFilter) &&
        (!this.categoryFilter || app.category === this.categoryFilter)
      );
    });
  }

  openCreateModal(): void {
    this.editingApp = null;
    this.resetForm();
    this.modalVisible = true;
  }

  openEditModal(app: AssistantApp): void {
    this.editingApp = app;
    this.resetIconSelection(app.iconUrl ?? '');
    this.form.patchValue({
      id: app.id,
      name: app.name,
      shortDescription: app.shortDescription ?? '',
      description: app.description,
      category: app.customCategory ? CUSTOM_CATEGORY : app.category,
      customCategory: app.customCategory ?? '',
      version: app.version,
      webhookUrl: app.webhookUrl,
      responseMode: app.responseMode,
      batchTriggerMessageCount: app.batchTrigger?.triggerMessageCount ?? 10,
      batchTriggerIntervalMinutes: (app.batchTrigger?.triggerIntervalSeconds ?? 3600) / 60,
      batchTriggerKeywordsText: (app.batchTrigger?.triggerKeywords ?? []).join(', '),
      messageTypes: app.messageTypes,
      senderTypes: app.senderTypes,
      replyReceiverTypes: app.replyReceiverTypes ?? [],
      featureTagsText: (app.featureTags ?? []).join(', ')
    });
    this.packages.clear();
    app.durationPackages.forEach(item => {
      this.packages.push(
        this.createPackage(
          item.name ?? this.defaultPackageName(item.durationDays),
          item.durationDays,
          item.priceCny,
          item.recommended,
          item.packageId
        )
      );
    });
    this.modalVisible = true;
  }

  closeModal(): void {
    if (!this.saving) this.modalVisible = false;
  }

  onIconFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      this.message.warning(this.t('assistant.store.message.icon-invalid-type'));
      input.value = '';
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      this.message.warning(this.t('assistant.store.message.icon-invalid-size'));
      input.value = '';
      return;
    }

    this.revokeIconObjectUrl();
    this.iconObjectUrl = URL.createObjectURL(file);
    this.iconPreview = this.iconObjectUrl;
    this.iconFile = file;
    input.value = '';
  }

  addPackage(): void {
    this.packages.push(this.createPackage('', 30, 0));
  }

  removePackage(index: number): void {
    if (this.packages.length > 1) this.packages.removeAt(index);
  }

  setRecommended(index: number): void {
    this.packages.controls.forEach((control, currentIndex) => control.get('recommended')?.setValue(currentIndex === index));
  }

  save(): void {
    if (this.form.invalid || this.packages.invalid) {
      this.form.markAllAsTouched();
      this.packages.markAllAsTouched();
      this.message.warning(this.validationErrorMessage());
      return;
    }
    const value = this.form.getRawValue();
    if (!value.id && !this.iconFile) {
      this.message.warning(this.t('assistant.store.message.icon-required'));
      return;
    }
    const payload: Record<string, unknown> = {
      id: value.id || undefined,
      name: value.name.trim(),
      shortDescription: value.shortDescription.trim(),
      description: value.description.trim(),
      category: value.category,
      customCategory: value.category === CUSTOM_CATEGORY ? value.customCategory.trim() : undefined,
      version: value.version.trim(),
      webhookUrl: value.webhookUrl.trim(),
      responseMode: value.responseMode,
      messageTypes: value.messageTypes,
      senderTypes: value.senderTypes,
      replyReceiverTypes: value.replyReceiverTypes,
      featureTags: this.parseTextList(value.featureTagsText)
    };
    if (value.responseMode === 'Batch') {
      payload['batchTrigger.triggerMessageCount'] = value.batchTriggerMessageCount;
      payload['batchTrigger.triggerIntervalSeconds'] = value.batchTriggerIntervalMinutes * 60;
      payload['batchTrigger.triggerKeywords'] = this.parseTextList(value.batchTriggerKeywordsText);
    }
    this.appendDurationPackageFields(payload);
    this.saving = true;
    this.http
      .postFormData(ConstantUtil.ADMIN_ASSISTANT_STORE_V1_SAVE, payload, this.iconFile, 'iconFile')
      .pipe(
        finalize(() => {
          this.saving = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: () => {
          this.message.success(this.t(this.editingApp ? 'assistant.store.message.updated' : 'assistant.store.message.created'));
          this.modalVisible = false;
          this.resetIconSelection();
          this.loadApps();
        },
        error: () => this.message.error(this.t('assistant.store.message.save-failed'))
      });
  }

  publish(app: AssistantApp): void {
    this.changeStatus(app, ConstantUtil.ADMIN_ASSISTANT_STORE_V1_PUBLISH, 'assistant.store.message.published');
  }

  unpublish(app: AssistantApp): void {
    this.changeStatus(app, ConstantUtil.ADMIN_ASSISTANT_STORE_V1_UNPUBLISH, 'assistant.store.message.unpublished');
  }

  statusLabel(status: AssistantAppStatus): string {
    return this.t(`assistant.store.status.${status.toLowerCase()}`);
  }

  getStatusCount(status: AssistantAppStatus): number {
    return this.apps.filter(app => app.status === status).length;
  }

  getActiveSubscriberCount(): number {
    return this.apps.reduce((total, app) => total + this.getSubscriberCount(app), 0);
  }

  getSubscriberCount(app: AssistantApp): number {
    return app.subscriberCount ?? app.subscriptionCount ?? 0;
  }

  formatPackageDurations(app: AssistantApp): string {
    const dayUnit = this.t('assistant.store.unit.day');
    return app.durationPackages.map(item => `${item.durationDays} ${dayUnit}`).join('、');
  }

  formatDate(value?: number): string {
    return value ? new Date(value).toLocaleString(this.i18n.currentLang || 'zh-TW', { hour12: false }) : '-';
  }

  trackByOperationsPackage(_: number, item: AssistantStoreOperationTemplatePackageKpi): string {
    return `${item.templateId}-${item.durationPackageId}`;
  }

  trackById(_: number, app: AssistantApp): string {
    return app.id;
  }

  categoryLabel(category: string): string {
    const categoryIndex = CATEGORIES.indexOf(category);
    return categoryIndex === -1 ? category : this.t(`assistant.store.category.${categoryIndex}`);
  }

  private updateCustomCategoryControl(category: string): void {
    const control = this.form.controls.customCategory;
    if (category === CUSTOM_CATEGORY) {
      control.setValidators(Validators.required);
    } else {
      control.clearValidators();
      control.setValue('', { emitEvent: false });
    }
    control.updateValueAndValidity({ emitEvent: false });
  }

  private changeStatus(app: AssistantApp, path: string, successMessageKey: string): void {
    this.operatingId = app.id;
    this.http
      .post(path.replace('{id}', app.id), {})
      .pipe(
        finalize(() => {
          this.operatingId = null;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: () => {
          this.message.success(this.t(successMessageKey));
          this.loadApps();
        },
        error: () => this.message.error(this.t('assistant.store.message.status-update-failed'))
      });
  }

  private resetForm(): void {
    this.resetIconSelection();
    this.form.reset({
      id: '',
      name: '',
      shortDescription: '',
      description: '',
      category: CATEGORIES[0],
      customCategory: '',
      version: '1.0',
      webhookUrl: '',
      responseMode: 'Qa',
      batchTriggerMessageCount: 10,
      batchTriggerIntervalMinutes: 60,
      batchTriggerKeywordsText: '',
      messageTypes: ['Text'],
      senderTypes: ['Customer'],
      replyReceiverTypes: [],
      featureTagsText: ''
    });
    this.packages.clear();
    [
      this.createPackage(this.defaultPackageName(30), 30, 99, true),
      this.createPackage(this.defaultPackageName(90), 90, 269),
      this.createPackage(this.defaultPackageName(365), 365, 999)
    ].forEach(item => this.packages.push(item));
  }

  private resetIconSelection(previewUrl = ''): void {
    this.revokeIconObjectUrl();
    this.iconFile = null;
    this.iconPreview = previewUrl;
  }

  private revokeIconObjectUrl(): void {
    if (!this.iconObjectUrl) return;
    URL.revokeObjectURL(this.iconObjectUrl);
    this.iconObjectUrl = '';
  }

  private createPackage(name: string, durationDays: number, priceCny: number, recommended = false, packageId = '') {
    return this.fb.nonNullable.group({
      packageId: [packageId],
      name: [name, [Validators.required, Validators.maxLength(50)]],
      durationDays: [durationDays, [Validators.required, Validators.min(1)]],
      priceCny: [priceCny, [Validators.required, Validators.min(0)]],
      recommended: [recommended]
    });
  }

  private appendDurationPackageFields(payload: Record<string, unknown>): void {
    this.packages.getRawValue().forEach((item, index) => {
      const prefix = `durationPackages[${index}]`;
      payload[`${prefix}.packageId`] = item.packageId || undefined;
      payload[`${prefix}.name`] = item.name.trim();
      payload[`${prefix}.durationDays`] = item.durationDays;
      payload[`${prefix}.priceCny`] = item.priceCny;
      payload[`${prefix}.recommended`] = item.recommended;
    });
  }

  private parseTextList(value: string): string[] {
    return value
      .split(/[,，\n]/)
      .map(item => item.trim())
      .filter(Boolean);
  }

  private defaultPackageName(days: number): string {
    return this.t('assistant.store.package.default-name', { days });
  }

  private validationErrorMessage(): string {
    const controls = this.form.controls;
    const invalidFields = [
      ['name', 'assistant.store.form.name'],
      ['description', 'assistant.store.form.description'],
      ['category', 'assistant.store.form.category'],
      ['customCategory', 'assistant.store.form.custom-category'],
      ['version', 'assistant.store.form.version'],
      ['webhookUrl', 'assistant.store.form.webhook-url'],
      ['responseMode', 'assistant.store.form.response-mode'],
      ['messageTypes', 'assistant.store.form.message-types'],
      ['senderTypes', 'assistant.store.form.sender-types']
    ]
      .filter(([controlName]) => controls[controlName as keyof typeof controls].invalid)
      .map(([, labelKey]) => this.t(labelKey));

    if (this.packages.invalid) invalidFields.push(this.t('assistant.store.section.packages'));
    return this.t('assistant.store.message.required-fields', { fields: invalidFields.join('、') });
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.i18n.fanyi(key, params);
  }

  isOptionSelected(controlName: 'messageTypes' | 'senderTypes' | 'replyReceiverTypes', value: string): boolean {
    return this.form.controls[controlName].value.includes(value);
  }

  toggleOption(controlName: 'messageTypes' | 'senderTypes' | 'replyReceiverTypes', value: string, checked: boolean): void {
    const currentValues = this.form.controls[controlName].value;
    const nextValues = checked ? [...new Set([...currentValues, value])] : currentValues.filter(item => item !== value);
    this.form.controls[controlName].setValue(nextValues);
  }

  private readApps(response: AssistantAppListResponse): AssistantApp[] {
    const data = response?.data;
    if (Array.isArray(data)) return data;
    return data?.list ?? data?.records ?? data?.items ?? [];
  }

  private createOperationsQuery(): Record<string, unknown> {
    const [startTime, endTime] = this.resolveOperationsDateRange();
    return {
      startTime: startTime.getTime(),
      endTime: endTime.getTime(),
      category: this.operationsCategory ?? undefined,
      keyword: this.operationsKeyword.trim() || undefined
    };
  }

  private resolveOperationsDateRange(): [Date, Date] {
    const now = new Date();
    if (this.operationsPeriod === 'custom' && this.operationsDateRange?.length === 2) {
      return [this.startOfDay(this.operationsDateRange[0]), this.endOfDay(this.operationsDateRange[1])];
    }
    if (this.operationsPeriod === 'today') return [this.startOfDay(now), this.endOfDay(now)];
    if (this.operationsPeriod === 'thisWeek') {
      const start = this.startOfDay(now);
      const weekday = start.getDay() || 7;
      start.setDate(start.getDate() - weekday + 1);
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      return [start, this.endOfDay(end)];
    }
    return [new Date(now.getFullYear(), now.getMonth(), 1), this.endOfDay(new Date(now.getFullYear(), now.getMonth() + 1, 0))];
  }

  private startOfDay(value: Date): Date {
    const result = new Date(value);
    result.setHours(0, 0, 0, 0);
    return result;
  }

  private endOfDay(value: Date): Date {
    const result = new Date(value);
    result.setHours(23, 59, 59, 999);
    return result;
  }

  private ensureOperationsRefreshTimer(): void {
    if (this.operationsRefreshTimer) return;
    this.operationsRefreshTimer = setInterval(
      () => {
        if (this.activeTab === 'operations') this.loadOperationsKpi();
      },
      60 * 60 * 1000
    );
  }

  private normalizeRatingDistribution(distribution?: AssistantStoreRatingItem[] | Record<string, number>): AssistantStoreRatingItem[] {
    return [5, 4, 3, 2, 1].map(stars => {
      const percentage = Array.isArray(distribution)
        ? distribution.find(item => Number(item.stars) === stars)?.percentage
        : distribution?.[String(stars)];
      return { stars, percentage: Math.max(0, Math.min(100, Number(percentage) || 0)) };
    });
  }

  private readOperationsKpi(response: AssistantStoreOperationKpiResponse): AssistantStoreOperationKpi {
    const data = response?.data;
    return {
      totalTemplateCount: data?.totalTemplateCount ?? 0,
      totalSubscriptionCount: data?.totalSubscriptionCount ?? 0,
      activeSubscriptionCount: data?.activeSubscriptionCount ?? data?.activeInstanceCount ?? 0,
      totalTransactionAmount: data?.totalTransactionAmount ?? 0,
      cashIncome: data?.cashIncome ?? 0,
      uuponDeduction: data?.uuponDeduction ?? 0,
      purchaseCount: data?.purchaseCount ?? 0,
      renewalCount: data?.renewalCount ?? 0,
      activeInstanceCount: data?.activeInstanceCount ?? 0,
      trialConversionCount: data?.trialConversionCount ?? 0,
      templates: data?.templates ?? [],
      templatePackages: data?.templatePackages ?? [],
      lastUpdatedTime: data?.lastUpdatedTime
    };
  }
}
